/**
 * Country Configuration Registry
 * 
 * Central registry for all supported country configurations.
 * This module exports all country configs and provides utilities
 * for working with country-specific settings.
 */

import { CountryConfig, CountryOption, DocumentRequirement } from './types';
import { zimbabweConfig } from './zimbabwe';

/**
 * Registry of all supported country configurations
 */
export const countryConfigs: Record<string, CountryConfig> = {
  ZW: zimbabweConfig,
  // Add more countries here as they are implemented
  // KE: kenyaConfig,
  // UG: ugandaConfig,
  // TZ: tanzaniaConfig,
};

/**
 * Get configuration for a specific country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Country configuration or undefined if not supported
 */
export function getCountryConfig(countryCode: string): CountryConfig | undefined {
  return countryConfigs[countryCode.toUpperCase()];
}

/**
 * Get list of all supported countries for selection
 * @returns Array of country options
 */
export function getSupportedCountries(): CountryOption[] {
  return Object.values(countryConfigs).map(config => ({
    code: config.code,
    name: config.name,
    enabled: true,
  }));
}

/**
 * Check if a country is supported
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns True if country is supported
 */
export function isCountrySupported(countryCode: string): boolean {
  return countryCode.toUpperCase() in countryConfigs;
}

/**
 * Get default country (first configured country)
 * @returns Default country configuration
 */
export function getDefaultCountry(): CountryConfig {
  const firstCountryCode = Object.keys(countryConfigs)[0];
  return countryConfigs[firstCountryCode];
}

/**
 * Get administrative level configuration for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @param level Administrative level number
 * @returns Administrative level configuration or undefined
 */
export function getAdministrativeLevel(countryCode: string, level: number) {
  const config = getCountryConfig(countryCode);
  return config?.administrativeLevels.find(l => l.level === level);
}

/**
 * Get payment method configuration for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @param paymentType Payment method type
 * @returns Payment method configuration or undefined
 */
export function getPaymentMethodConfig(countryCode: string, paymentType: string) {
  const config = getCountryConfig(countryCode);
  return config?.paymentMethods.find(pm => pm.type === paymentType && pm.enabled);
}

/**
 * Get all enabled payment methods for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Array of enabled payment method configurations
 */
export function getEnabledPaymentMethods(countryCode: string) {
  const config = getCountryConfig(countryCode);
  return config?.paymentMethods.filter(pm => pm.enabled) || [];
}

/**
 * Get document requirements for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Array of document requirements
 */
export function getDocumentRequirements(countryCode: string) {
  const config = getCountryConfig(countryCode);
  return config?.documentRequirements || [];
}

/**
 * Get universal document requirements that apply to all countries
 * @returns Array of universal document requirements
 */
export function getUniversalDocumentRequirements(): DocumentRequirement[] {
  return [
    {
      type: 'proof_of_stake',
      name: 'Proof of Stake/Insurable Interest',
      required: true,
      acceptedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 10 * 1024 * 1024, // 10MB
      description: 'Document showing your legal right to insure the land/crop (e.g., land title, lease agreement, chief\'s letter, loan agreement)',
    },
    {
      type: 'national_id',
      name: 'National ID',
      required: false,
      acceptedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024, // 5MB
      description: 'Copy of national identification document',
    },
  ];
}

/**
 * Get boundary box for coordinate validation for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Boundary box or null if not configured
 */
export function getCountryBoundaryBox(countryCode: string) {
  const config = getCountryConfig(countryCode);
  return config?.validationRules?.boundaryBox || null;
}

/**
 * Validate phone number for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @param phoneNumber Phone number to validate
 * @returns True if phone number is valid for the country
 */
export function validatePhoneNumber(countryCode: string, phoneNumber: string): boolean {
  const config = getCountryConfig(countryCode);
  if (!config?.validationRules?.phonePattern) {
    return true; // No specific validation rule
  }
  
  const regex = new RegExp(config.validationRules.phonePattern);
  return regex.test(phoneNumber);
}

/**
 * Get agricultural activities for a country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Array of agricultural activities
 */
export function getAgriculturalActivities(countryCode: string): string[] {
  const config = getCountryConfig(countryCode);
  return config?.agriculturalActivities || [];
}

// Re-export types and configurations for easy access
export * from './types';
export { zimbabweConfig };

// Default export for convenience
export default {
  getCountryConfig,
  getSupportedCountries,
  isCountrySupported,
  getDefaultCountry,
  getAdministrativeLevel,
  getPaymentMethodConfig,
  getEnabledPaymentMethods,
  getDocumentRequirements,
  validatePhoneNumber,
  getAgriculturalActivities,
};